<script setup lang="ts">
import { computed, ref } from "vue";
import { useRoute } from "vue-router";
import { useToast } from "vue-toastification";
import { v4 as uuidv4 } from "uuid";

import gaiupApolloClient from "@/graphql/services/ApolloClient";
import UpsertProjectWasteMapping from "@/graphql/mutations/UpsertProjectWasteMappingMutation";
import UpsertProjectWasteMappingWithVersion from "@/graphql/mutations/UpsertProjectWasteMappingWithVersionMutation";
import { isVersioningSchemaMissing } from "@/pages/ProjectVersionHistory/services/versioningSchema";
import { useProjectEditReadOnly } from "@/pages/ProjectVersionHistory/composables/useProjectEditReadOnly.composable";
import RevisionGatedButton from "@/pages/ProjectVersionHistory/components/revisions/RevisionGatedButton.vue";
import { buildWasteVersionInput } from "../utils/wasteVersionSnapshot";

import Button from "@/components/Reusables/Buttons/Button.vue";
import { useModalStore } from "@/stores/modals/modals.store";
import format from "@/services/Utils/format";
import type { ProjectWasteMappingFragment } from "@/graphql";

interface EditableRow {
  id: string;
  label: string;
  materialId: string;
  materialWasteTypeId?: string | null;
  pickupDate?: string | null;
  originalWeight: number;
  customWeight: number;
  customWasteType?: { id: string; label: string } | null;
  wasteTypeName: string;
  isCustom: boolean;
  lockedPercentage?: number | null;
}

interface MaterialGroup {
  materialId: string;
  materialName: string;
  snapshotWeight: number | null;
  currentWeight: number;
  rows: EditableRow[];
}

interface WasteTypeGrouping {
  name: string;
  isCustom: boolean;
  rows: EditableRow[];
}

const props = defineProps<{
  materialIds?: string[] | null;
  enrichedMappings: ProjectWasteMappingFragment[];
  weightByMaterialId: Map<string, number>;
  wasteTypeLabelById: Map<string, string>;
  lockedPercentageByWasteTypeId?: Map<string, number>;
  emissionByWasteMappingId?: Map<string, { original: number; scaled: number }>;
  emissionUnit?: string;
  weightUnit?: "kg" | "ton";
  onSaved?: () => Promise<void>;
}>();

const route = useRoute();
const modals = useModalStore();
const toast = useToast();

// Both triggers ("Review amounts", and a card's "Review") are blocked for a Viewer and while a
// frozen revision is previewed, so this guards the modal left open across a change in either. Row
// edits stay live — they are local until Update — but Update itself is the write and refuses.
const { blockedByPreview } = useProjectEditReadOnly();

const isSaving = ref(false);
const collapsedGroups = ref(new Set<string>());

const formatWeight = (weight: number): number => {
  if (props.weightUnit === "ton") {
    return weight / 1000;
  }
  return weight;
};

const getWeightUnit = (): string => {
  return props.weightUnit === "ton" ? "ton" : "kg";
};

const toggleGroup = (key: string) => {
  const next = new Set(collapsedGroups.value);
  if (next.has(key)) {
    next.delete(key);
  } else {
    next.add(key);
  }
  collapsedGroups.value = next;
};

const materialGroups = computed((): MaterialGroup[] => {
  const byMaterial = new Map<string, ProjectWasteMappingFragment[]>();
  for (const mapping of props.enrichedMappings) {
    if (!mapping.materialId) continue;
    const existing = byMaterial.get(mapping.materialId) ?? [];
    existing.push(mapping);
    byMaterial.set(mapping.materialId, existing);
  }

  const groups: MaterialGroup[] = [];
  for (const [materialId, mappings] of byMaterial) {
    const currentWeight = props.weightByMaterialId.get(materialId);
    if (currentWeight === undefined) continue;

    const snapshotMapping = mappings.find((mapping) => mapping.materialWeightSnapshot != null);
    const snapshotWeight = snapshotMapping ? Number(snapshotMapping.materialWeightSnapshot) : null;
    const isOutOfSync = snapshotWeight != null && Math.abs(snapshotWeight - currentWeight) > 0.01;

    if (props.materialIds != null) {
      if (!props.materialIds.includes(materialId)) continue;
    } else {
      if (!isOutOfSync) continue;
    }

    const weightScale = snapshotWeight && snapshotWeight > 0 ? currentWeight / snapshotWeight : 1;

    groups.push({
      materialId,
      materialName: mappings[0]?.label ?? materialId,
      snapshotWeight,
      currentWeight,
      rows: mappings.map((mapping) => {
        const locked =
          !mapping.customWasteType && mapping.materialWasteTypeId
            ? props.lockedPercentageByWasteTypeId?.get(mapping.materialWasteTypeId) ?? null
            : null;
        const scaledWeight =
          locked != null
            ? parseFloat(((locked / 100) * currentWeight).toFixed(2))
            : parseFloat(((mapping.customWeight ?? 0) * weightScale).toFixed(2));
        return {
          id: mapping.id,
          label: mapping.label ?? "",
          materialId,
          materialWasteTypeId: mapping.materialWasteTypeId,
          pickupDate: mapping.pickupDate,
          originalWeight: mapping.customWeight ?? 0,
          customWeight: scaledWeight,
          customWasteType: mapping.customWasteType ?? null,
          wasteTypeName:
            mapping.customWasteType?.label ??
            (mapping.materialWasteTypeId ? props.wasteTypeLabelById.get(mapping.materialWasteTypeId) ?? "Unknown" : "Unknown"),
          isCustom: !!mapping.customWasteType,
          lockedPercentage: locked,
        };
      }),
    });
  }
  return groups;
});

const editableRows = ref<EditableRow[]>([]);

// Initialize editable rows from computed groups
const initRows = () => {
  editableRows.value = materialGroups.value.flatMap((group) => group.rows);
};
initRows();

const editableMaterialGroups = computed(() => {
  const rowMap = new Map<string, EditableRow>();
  for (const row of editableRows.value) {
    rowMap.set(row.id, row);
  }

  return materialGroups.value.map((group) => ({
    ...group,
    rows: group.rows.map((row) => rowMap.get(row.id) ?? row),
  }));
});

const getWasteTypeGroups = (rows: EditableRow[]): WasteTypeGrouping[] => {
  const map = new Map<string, EditableRow[]>();
  for (const row of rows) {
    const key = row.wasteTypeName || "Unknown";
    const existing = map.get(key) ?? [];
    existing.push(row);
    map.set(key, existing);
  }
  return [...map.entries()]
    .map(([name, groupRows]) => ({
      name,
      isCustom: groupRows[0]?.isCustom ?? false,
      rows: groupRows,
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
};

const allWasteTypeGroups = computed(() => getWasteTypeGroups(editableRows.value));

const getEmission = (rowId: string) => props.emissionByWasteMappingId?.get(rowId);

const getPercentage = (weight: number, materialId: string) => {
  const materialWeight = props.weightByMaterialId.get(materialId);
  if (!materialWeight || materialWeight <= 0) return null;
  return parseFloat(((weight / materialWeight) * 100).toFixed(2));
};

const openPickupEdit = async (row: EditableRow) => {
  if (row.lockedPercentage != null) return;
  const materialWeight = props.weightByMaterialId.get(row.materialId);
  const result = await modals.open(
    "WasteSyncPickupEditModal",
    {
      label: row.label,
      weight: row.customWeight,
      materialWeight,
      weightUnit: props.weightUnit,
    },
    { stackable: true, wrapperClass: "max-w-lg h-fit min-h-0" }
  );

  if (result?.weight !== undefined) {
    const index = editableRows.value.findIndex((editableRow) => editableRow.id === row.id);
    if (index >= 0) {
      editableRows.value[index] = { ...editableRows.value[index], customWeight: result.weight };
    }
  }
};

const handleSave = async () => {
  if (blockedByPreview()) return;
  isSaving.value = true;
  try {
    const objects = editableRows.value.map((row) => ({
      id: row.id ?? uuidv4(),
      projectId: String(route.params.id),
      label: row.label || undefined,
      materialId: row.materialId,
      materialWasteTypeId: row.materialWasteTypeId ?? undefined,
      pickupDate: row.pickupDate ?? undefined,
      customWeight: row.customWeight ?? 0,
      materialWeightSnapshot: props.weightByMaterialId.get(row.materialId),
      ...(row.customWasteType ? { projectWasteTypeCustomId: row.customWasteType.id } : {}),
    }));

    // The sync is one change-burst — one bundled mutation, one trail row. The trail is a
    // display-only log; the revision's waste STATE is the rows themselves, revision-scoped.
    const version = buildWasteVersionInput({
      projectId: String(route.params.id),
      operation: "update",
      summary: `Waste amounts synced to updated material weights (${objects.length} sortings)`,
    });

    try {
      await gaiupApolloClient.mutate({
        mutation: UpsertProjectWasteMappingWithVersion,
        variables: { objects, version },
      });
    } catch (mutationError) {
      // Versioning migration not applied here yet — fall back to the plain upsert
      // (rollout: Hasura → API → UI).
      if (!isVersioningSchemaMissing(mutationError)) throw mutationError;
      await gaiupApolloClient.mutate({
        mutation: UpsertProjectWasteMapping,
        variables: { objects },
      });
    }

    if (props.onSaved) await props.onSaved();

    toast.success("Waste sortings updated");
    modals.closeByName("WasteSyncAmountsModal");
  } catch (error: unknown) {
    console.error("Error updating waste sortings:", error);
    toast.error("Failed to update waste sortings");
  } finally {
    isSaving.value = false;
  }
};

const handleSkip = () => {
  modals.closeByName("WasteSyncAmountsModal");
};
</script>

<template>
  <div class="flex min-h-75 flex-col gap-5">
    <div class="flex flex-col gap-2">
      <h2 class="t-sh4 text-type-default flex items-center gap-2">
        <font-awesome-icon
          icon="fa-solid fa-arrows-rotate"
          class="text-app-green-500" />
        Review Amounts
      </h2>
      <p class="t-small text-type-muted">
        The following materials have changed weight since their waste sortings were last saved. The waste sorting weights have
        been adjusted proportionally. You can review and change the values below.
      </p>

      <div class="rounded-8 flex flex-col gap-1 border border-slate-200 px-3 py-2">
        <div
          v-for="materialGroup in editableMaterialGroups"
          :key="materialGroup.materialId"
          class="flex items-center justify-between py-1">
          <span class="t-small-bold text-type-default">{{ materialGroup.materialName }}</span>
          <span class="t-small text-type-muted">
            <span class="text-type-muted">{{
              materialGroup.snapshotWeight != null
                ? `${format.decimalNumber(formatWeight(materialGroup.snapshotWeight), 2)} ${getWeightUnit()}`
                : "—"
            }}</span>
            <font-awesome-icon
              icon="fa-solid fa-arrow-right"
              class="text-type-muted px-1" />
            <span class="t-small-bold text-type-default"
              >{{ format.decimalNumber(formatWeight(materialGroup.currentWeight), 2) }} {{ getWeightUnit() }}</span
            >
            <span
              class="pl-1"
              :class="
                materialGroup.currentWeight >= (materialGroup.snapshotWeight ?? 0) ? 'text-app-red-500' : 'text-app-green-500'
              ">
              ({{ materialGroup.currentWeight >= (materialGroup.snapshotWeight ?? 0) ? "+" : ""
              }}{{ format.decimalNumber(formatWeight(materialGroup.currentWeight - (materialGroup.snapshotWeight ?? 0)), 2) }}
              {{ getWeightUnit() }})
            </span>
          </span>
        </div>
      </div>
    </div>

    <div class="flex flex-col gap-3 overflow-y-auto">
      <div
        v-for="wasteGroup in allWasteTypeGroups"
        :key="wasteGroup.name"
        class="bg-app-green-500/10 rounded-8 t-small flex overflow-hidden border border-slate-200">
        <!-- Status bar -->
        <div class="mx-3 flex w-5 shrink-0 flex-col py-3">
          <span class="bg-app-green-100 rounded-4 w-full flex-1"></span>
        </div>

        <!-- Content -->
        <div class="flex w-full flex-col pr-3">
          <!-- Group header -->
          <div
            class="flex w-full cursor-pointer items-center justify-between gap-2 py-3 text-slate-500"
            role="button"
            tabindex="0"
            :aria-expanded="!collapsedGroups.has(wasteGroup.name)"
            @click="toggleGroup(wasteGroup.name)"
            @keydown.enter="toggleGroup(wasteGroup.name)"
            @keydown.space.prevent="toggleGroup(wasteGroup.name)">
            <div class="t-small-bold flex flex-col gap-0.5">
              <span class="truncate">{{ wasteGroup.name }}</span>
              <span class="t-caption">Sortings: {{ wasteGroup.rows.length }}</span>
            </div>
            <font-awesome-icon
              :icon="collapsedGroups.has(wasteGroup.name) ? 'fa-solid fa-chevron-down' : 'fa-solid fa-chevron-up'"
              class="t-xs pr-1 text-slate-400" />
          </div>

          <!-- Pickup rows -->
          <div
            v-if="!collapsedGroups.has(wasteGroup.name)"
            class="last:pb-3">
            <div
              v-for="(row, rowIndex) in wasteGroup.rows"
              :key="row.id"
              class="t-caption-bold duration-normal ease-standard grid grid-cols-[minmax(0,3fr)_auto_2fr_1fr_2fr] items-center gap-x-3 py-3 pr-2 pl-3 text-slate-500 transition-colors"
              :class="[
                rowIndex % 2 === 0 ? 'bg-app-green-50' : '',
                row.lockedPercentage != null ? 'opacity-60' : 'cursor-pointer hover:brightness-95',
              ]"
              role="button"
              tabindex="0"
              :aria-label="`Edit ${row.label}`"
              @click="openPickupEdit(row)"
              @keydown.enter="openPickupEdit(row)"
              @keydown.space.prevent="openPickupEdit(row)">
              <span class="truncate">{{ row.label.length > 20 ? row.label.slice(0, 20) + "..." : row.label }}</span>
              <span
                v-if="row.originalWeight !== row.customWeight"
                class="text-right whitespace-nowrap text-slate-400 line-through">
                {{ format.decimalNumber(formatWeight(row.originalWeight), 2) }} {{ getWeightUnit() }}
              </span>
              <span
                v-else
                class="whitespace-nowrap" />
              <span class="text-right whitespace-nowrap"
                >{{ format.decimalNumber(formatWeight(row.customWeight), 2) }} {{ getWeightUnit() }}</span
              >
              <span
                v-if="getPercentage(row.customWeight, row.materialId) !== null"
                class="text-right whitespace-nowrap text-slate-400">
                {{ getPercentage(row.customWeight, row.materialId) }}%
              </span>
              <span v-else />
              <span
                v-if="getEmission(row.id)"
                class="text-right whitespace-nowrap">
                <strong class="pr-[2px]">{{ format.decimalNumber(getEmission(row.id)!.scaled) }}</strong>
                <small class="font-semibold">{{ emissionUnit ?? "-" }}</small>
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="pt-px-8 mt-auto flex justify-between">
      <Button
        label="Skip"
        color="button"
        :disabled="isSaving"
        @click="handleSkip" />
      <RevisionGatedButton
        :label="isSaving ? 'Saving...' : 'Update'"
        :is-loading="isSaving"
        color="primary"
        :disabled="isSaving"
        :guard="useProjectEditReadOnly"
        @click="handleSave" />
    </div>
  </div>
</template>
